import { useState } from "react";
import { Wallet, Search, X, Edit3, Check, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletsTab = ({ regularUsers, onUserUpdated }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [editingWallet, setEditingWallet] = useState(null);
  const [walletInput, setWalletInput] = useState("");
  const [saving, setSaving] = useState(false);

  // Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // Filter users by search query
  const filteredUsers = regularUsers.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query) ||
      user.wallet_address?.toLowerCase().includes(query)
    );
  });

  const handleEditWallet = (user) => {
    setEditingWallet(user.id);
    setWalletInput(user.wallet_address || "");
  };

  const handleSaveWallet = async (userId) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/mnro/users/${userId}/wallet?wallet_address=${encodeURIComponent(walletInput)}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Кошелёк обновлён");
      setEditingWallet(null);
      setWalletInput("");
      if (onUserUpdated) onUserUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка при обновлении кошелька");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingWallet(null);
    setWalletInput("");
  };

  return (
    <div>
      <h2 className="font-chivo font-bold text-xl text-white mb-2">Кошельки пользователей</h2>
      <p className="text-[#A1A1AA] text-sm mb-6">
        Установите персональные адреса кошельков для пополнения баланса каждого пользователя
      </p>
      
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        {/* Search bar */}
        <div className="p-4 border-b border-[#1F1F1F]">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ID, имени, email или кошельку..."
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-10 py-2.5 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-[#A1A1AA]">
              Найдено: {filteredUsers.length} из {regularUsers.length} пользователей
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Пользователь</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Адрес кошелька (TRC-20)</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4 w-32">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-12 text-[#A1A1AA]">
                    {searchQuery ? "Пользователи не найдены" : "Нет пользователей"}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50">
                    <td className="py-4 px-4">
                      <div className="text-white font-medium">{u.name}</div>
                      <div className="text-[#A1A1AA] text-sm">{u.email}</div>
                    </td>
                    <td className="py-4 px-4">
                      {editingWallet === u.id ? (
                        <input
                          type="text"
                          value={walletInput}
                          onChange={(e) => setWalletInput(e.target.value)}
                          placeholder="Введите адрес TRC-20 кошелька..."
                          className="w-full bg-[#000000] border border-white rounded px-3 py-2 text-white font-mono text-sm focus:outline-none"
                          autoFocus
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          {u.wallet_address ? (
                            <>
                              <Wallet size={16} className="text-[#34D399] flex-shrink-0" />
                              <code className="text-[#34D399] font-mono text-sm break-all">
                                {u.wallet_address}
                              </code>
                            </>
                          ) : (
                            <span className="text-[#A1A1AA] text-sm italic">
                              Не установлен (используется общий)
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex gap-2">
                        {editingWallet === u.id ? (
                          <>
                            <button
                              onClick={() => handleSaveWallet(u.id)}
                              disabled={saving}
                              className="flex items-center gap-1.5 bg-[#34D399]/15 text-[#34D399] px-3 py-1.5 rounded text-sm hover:bg-[#34D399]/25 transition-colors disabled:opacity-50"
                              title="Сохранить"
                            >
                              <Check size={14} />
                              {saving ? "..." : "Сохранить"}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-[#A1A1AA] hover:text-white p-1.5"
                              title="Отмена"
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEditWallet(u)}
                            className="flex items-center gap-1.5 bg-white/15 text-[#EDEDED] px-3 py-1.5 rounded text-sm hover:bg-[#1A1A1A]/25 transition-colors"
                            title="Редактировать кошелёк"
                          >
                            <Edit3 size={14} />
                            {u.wallet_address ? "Изменить" : "Добавить"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Info box */}
      <div className="mt-6 bg-white/10 border border-white/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Wallet className="text-[#EDEDED] flex-shrink-0 mt-0.5" size={20} />
          <div>
            <div className="text-[#EDEDED] font-bold mb-1">Как это работает?</div>
            <ul className="text-[#A1A1AA] text-sm space-y-1">
              <li>• Если кошелёк не установлен — пользователь видит общий адрес платформы</li>
              <li>• После установки персонального кошелька — он отображается на странице пополнения</li>
              <li>• Можно изменить или удалить адрес в любое время (оставьте поле пустым для удаления)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletsTab;
